import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';

const Container = styled(motion.div)`
  min-height: 100vh;
  background: var(--background-start);
  padding-top: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ResetPasswordBox = styled.div`
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-color);
  border-radius: 15px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 30px;
  font-size: 2rem;
  font-weight: 700;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  margin-bottom: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--gradient-pink);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(90deg, var(--gradient-purple), var(--gradient-pink));
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 0, 127, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 20px;
  color: var(--secondary-color);
  text-decoration: none;
  
  &:hover {
    color: var(--gradient-cyan);
  }
`;

const Message = styled.div`
  color: ${props => props.success ? '#2ed573' : '#ff6b6b'};
  background: ${props => props.success ? 'rgba(46, 213, 115, 0.1)' : 'rgba(255, 107, 107, 0.1)'};
  border: 1px solid ${props => props.success ? 'rgba(46, 213, 115, 0.3)' : 'rgba(255, 107, 107, 0.3)'};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
  font-size: 0.9rem;
  text-align: center;
`;

const RequirementsBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  font-size: 0.85rem;
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: ${props => props.valid ? '#2ed573' : 'var(--secondary-color)'};
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &::before {
    content: '${props => props.valid ? '✓' : '○'}';
    margin-right: 10px;
    font-weight: bold;
    font-size: 1.1rem;
  }
`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setMessage('Geçersiz token. Lütfen email\'inizdeki linki kullanın.');
    }
  }, [token]);

  // Şifre gereksinimlerini kontrol et
  useEffect(() => {
    const password = formData.password;
    setPasswordRequirements({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password)
    });
  }, [formData.password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setSuccess(false);

    // Şifre gereksinimleri kontrolü
    const requirements = passwordRequirements;
    if (!requirements.minLength || !requirements.hasUpperCase || 
        !requirements.hasLowerCase || !requirements.hasNumber) {
      setMessage('Şifre tüm gereksinimleri karşılamıyor. Lütfen kontrol edin.');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setMessage('Şifreler eşleşmiyor');
      setLoading(false);
      return;
    }

    if (!token) {
      setMessage('Geçersiz token');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.RESET_PASSWORD), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token: token,
          password: formData.password
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setMessage('Şifreniz başarıyla güncellendi! Giriş sayfasına yönlendiriliyorsunuz...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setMessage(data.message || 'Şifre sıfırlama sırasında bir hata oluştu');
      }
    } catch (error) {
      setMessage('Şifre sıfırlama sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Container
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <ResetPasswordBox>
          <Title>Yeni Şifre Belirle</Title>
          <form onSubmit={handleSubmit}>
            <Input
              type="password"
              placeholder="Yeni Şifre"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required
              minLength={8}
            />
            
            {formData.password && (
              <RequirementsBox>
                <div style={{ marginBottom: '10px', fontWeight: '600', color: 'var(--primary-color)' }}>
                  Şifre Gereksinimleri:
                </div>
                <RequirementItem valid={passwordRequirements.minLength}>
                  En az 8 karakter
                </RequirementItem>
                <RequirementItem valid={passwordRequirements.hasUpperCase}>
                  En az 1 büyük harf (A-Z)
                </RequirementItem>
                <RequirementItem valid={passwordRequirements.hasLowerCase}>
                  En az 1 küçük harf (a-z)
                </RequirementItem>
                <RequirementItem valid={passwordRequirements.hasNumber}>
                  En az 1 rakam (0-9)
                </RequirementItem>
              </RequirementsBox>
            )}
            
            <Input
              type="password"
              placeholder="Yeni Şifre (Tekrar)"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              required
              minLength={8}
            />
            {message && <Message success={success}>{message}</Message>}
            <Button type="submit" disabled={loading || success || !token}>
              {loading ? 'Güncelleniyor...' : success ? 'Başarılı!' : 'Şifreyi Güncelle'}
            </Button>
          </form>
          <BackLink to="/login">
            Giriş sayfasına dön
          </BackLink>
        </ResetPasswordBox>
      </Container>
    </>
  );
};

export default ResetPassword;

